import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "../../../lib/utils";

export interface SystemTooltipProps {
  /** The element that triggers the tooltip on hover */
  children: React.ReactNode;
  /** Tooltip text content */
  content: string;
  /** Position of the tooltip relative to the trigger */
  side?: "top" | "right" | "bottom" | "left";
  /** Alignment of the tooltip relative to the trigger */
  align?: "start" | "center" | "end";
  /** Distance in pixels from the trigger */
  sideOffset?: number;
  /** Delay in ms before showing the tooltip */
  delayDuration?: number;
  /** Whether the tooltip is disabled */
  disabled?: boolean;
  /** Additional className for the tooltip content */
  className?: string;
}

/**
 * SystemTooltipContent - The actual tooltip content component
 * Black background tooltip matching Capsule Design System
 *
 * Based on Figma: System Tooltip component
 * https://www.figma.com/design/j1q6kG0YaKkt7ASWZLrvE9/Capsule-DS--New-?node-id=15165-38
 */
const SystemTooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, children, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        // Base styles - black background, white text
        "z-50 rounded bg-black px-2 py-[1px]",
        "text-xs leading-4 text-white text-center whitespace-nowrap",
        // Shadow matching Figma design
        "shadow-[0px_4px_8px_rgba(2,0,94,0.1),0px_2px_4px_rgba(2,0,94,0.06)]",
        // Animation
        "animate-in fade-in-0 zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2",
        "data-[side=left]:slide-in-from-right-2",
        "data-[side=right]:slide-in-from-left-2",
        "data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    >
      {children}
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
SystemTooltipContent.displayName = "SystemTooltipContent";

/**
 * SystemTooltip - A tooltip component following Capsule Design System
 *
 * Features:
 * - Black background with white text
 * - Smooth animation on show/hide
 * - Configurable position and alignment
 * - Delay before showing
 *
 * Based on Figma: System Tooltip component
 *
 * @example
 * ```tsx
 * // Basic usage
 * <SystemTooltip content="Copy to clipboard">
 *   <button>Copy</button>
 * </SystemTooltip>
 *
 * // With custom position
 * <SystemTooltip content="Settings" side="right" align="start">
 *   <IconButton icon={<SettingsIcon />} />
 * </SystemTooltip>
 * ```
 */
function SystemTooltip({
  children,
  content,
  side = "top",
  align = "center",
  sideOffset = 4,
  delayDuration = 100,
  disabled = false,
  className,
}: SystemTooltipProps) {
  // If disabled or no content, just render children
  if (disabled || !content) {
    return <>{children}</>;
  }

  return (
    <TooltipPrimitive.Provider delayDuration={delayDuration}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <SystemTooltipContent
          side={side}
          align={align}
          sideOffset={sideOffset}
          className={className}
        >
          {content}
        </SystemTooltipContent>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

SystemTooltip.displayName = "SystemTooltip";

// Export both the full tooltip and the content component for advanced use cases
export { SystemTooltip, SystemTooltipContent };
